package br.gov.go.pge.permanenciaweb.site.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import br.gov.go.pge.permanenciaweb.site.IndustrialPalette
import br.gov.go.pge.permanenciaweb.site.pages.DashboardState
import br.gov.go.pge.permanenciaweb.site.pages.loadSemSaida
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxHeight
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.forms.ButtonSize
import com.varabyte.kobweb.silk.components.overlay.Overlay
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.window
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Table
import org.jetbrains.compose.web.dom.Tbody
import org.jetbrains.compose.web.dom.Td
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Th
import org.jetbrains.compose.web.dom.Thead
import org.jetbrains.compose.web.dom.Tr
import org.w3c.dom.events.EventListener
import org.w3c.dom.events.KeyboardEvent

val SemSaidaDialogStyle = CssStyle.base {
    Modifier
        .backgroundColor(IndustrialPalette.White)
        // 1px hairline borders stay in px per the units policy.
        .border(1.px, LineStyle.Solid, IndustrialPalette.Border)
        .borderRadius(0.375.cssRem)
        .padding(1.25.cssRem)
        .width(100.percent)
        .maxWidth(44.cssRem)
        .maxHeight(80.percent)
        .overflow(Overflow.Auto)
}

val SemSaidaDialogTitleStyle = CssStyle.base {
    Modifier.margin(0.cssRem)
}

val SemSaidaDialogActionsStyle = CssStyle.base {
    Modifier
        .display(DisplayStyle.Flex)
        .justifyContent(JustifyContent.FlexEnd)
        .gap(0.5.cssRem)
        .margin(top = 1.cssRem)
}

val SemSaidaDialogTableStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .maxHeight(50.cssRem)
        .overflow(Overflow.Auto)
}

@Composable
internal fun SemSaidaDialog(state: DashboardState) {
    val idpessoa = state.semSaidaDetailId ?: return

    LaunchedEffect(idpessoa) {
        if (!state.semSaidaLoaded) state.loadSemSaida()
    }

    val row = state.semSaidaRows.firstOrNull { it.idpessoa == idpessoa }

    // Escape hatch: Compose HTML has no key-event composable; a window keydown listener (removed on
    // dispose) implements ESC-to-close, matching the removal dialog.
    DisposableEffect(idpessoa) {
        val listener = EventListener { event ->
            if ((event as? KeyboardEvent)?.key == "Escape") state.closeSemSaidaDetail()
        }
        window.addEventListener("keydown", listener)
        onDispose { window.removeEventListener("keydown", listener) }
    }

    Overlay(contentAlignment = Alignment.Center) {
        // Full-size sibling backdrop: clicks outside the card close it; clicks on the card never reach it.
        Box(Modifier.fillMaxSize().onClick { state.closeSemSaidaDetail() })

        Column(
            modifier = SemSaidaDialogStyle.toModifier().then(Modifier.id("sem-saida-detail")),
        ) {
            H3(attrs = SemSaidaDialogTitleStyle.toAttrs()) {
                Text(if (row != null) "Sem saída — ${row.nome}" else "Sem saída")
            }

            if (row == null) {
                val error = state.semSaidaLoadError
                P {
                    Text(
                        when {
                            state.semSaidaLoading -> "Carregando…"
                            error != null -> error
                            else -> "Sem detalhes para esta pessoa."
                        }
                    )
                }
            } else {
                P {
                    Text("${row.diasSemSaida} dia(s) sem saída registrada(s) nos últimos 30 dias.")
                }
                if (row.dias.isEmpty()) {
                    P { Text("Nenhum dia registrado.") }
                } else {
                    Div(attrs = SemSaidaDialogTableStyle.toAttrs()) {
                        Table(attrs = TableStyle.toAttrs()) {
                            Thead {
                                Tr {
                                    Th(attrs = TableHeaderCellStyle.toAttrs { attr("scope", "col") }) {
                                        Text("Dia")
                                    }
                                    Th(attrs = TableHeaderCellStyle.toAttrs { attr("scope", "col") }) {
                                        Text("Último Entrou")
                                    }
                                }
                            }
                            Tbody {
                                for (day in row.dias) {
                                    Tr(attrs = TableRowHoverStyle.toAttrs()) {
                                        Td(attrs = TableCellStyle.toAttrs()) { Text(day.dia) }
                                        Td(attrs = TableCellStyle.toAttrs()) { Text(day.entradaEm) }
                                    }
                                }
                            }
                        }
                    }
                }
            }

            Div(attrs = SemSaidaDialogActionsStyle.toAttrs()) {
                Button(
                    onClick = { state.closeSemSaidaDetail() },
                    variant = DefaultButtonVariant,
                    size = ButtonSize.SM,
                ) {
                    Text("Fechar")
                }
            }
        }
    }
}
